/**
 * CR413: Hook for promoting deal card config between environments.
 * Wraps the promote_deal_card_config RPC.
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PublishEnvironment = "staging" | "preview" | "production";

export interface PromoteResult {
  environment: string;
  deal_type: string;
  active_version_id: string;
  created_at: string;
  created_by: string | null;
  source_version_id: string | null;
}

export function usePromoteDealCardConfig() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const promote = useCallback(async (
    fromEnv: PublishEnvironment,
    toEnv: PublishEnvironment,
    dealType: string,
    expectedToActiveVersionId?: string,
  ): Promise<PromoteResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "promote_deal_card_config" as any,
        {
          p_from_environment: fromEnv,
          p_to_environment: toEnv,
          p_deal_type: dealType,
          p_expected_to_active_version_id: expectedToActiveVersionId ?? null,
        } as any,
      );

      if (rpcError) {
        if (rpcError.message?.includes("CONFLICT:")) {
          setError("CONFLICT");
          return null;
        }
        setError(rpcError.message);
        console.error("[CR413] promote error:", rpcError);
        return null;
      }

      const row = Array.isArray(data) ? data[0] : data;
      return {
        environment: row.environment,
        deal_type: row.deal_type,
        active_version_id: row.active_version_id,
        created_at: row.created_at,
        created_by: row.created_by ?? null,
        source_version_id: row.source_version_id ?? null,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      console.error("[CR413] promote error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { promote, loading, error, clearError: useCallback(() => setError(null), []) };
}
